import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {SchemasOrderSubscription} from '@shared/schemas/order'

import {changeLocation} from '@web/actions/application'
import {navigateToShoppingSubscriptionFail} from '@web/actions/navigateTo'
import {saveStateToStorage} from '@web/actions/restoreState'
import getApi, {createWrapper} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'
import {deviceType, isMobileDisplay, osType} from '@web/helpers/userAgent'
import {commonSelector} from '@web/slices/common/slice'

import {
    FailCode,
    SUBSCRIPTION_BASE_PATH,
    SubscriptionProcessExceptionCode,
    SubscriptionProcessExceptionCodeValues,
} from '../../constants'
import {FailSessionStorageInterface, ApplyRootState as ApplyState} from '../../interfaces'

import {ApplyPayload, ApplyRequest, FetchApplyStatusPayload, FetchApplyStatusRequest} from './interfaces'

const api = getApi('/orderApi')

const API_BASE_URL = '/orderSheet/subscription/apply'

export const fetchApplyStatus = createAsyncThunk<
    FetchApplyStatusPayload,
    FetchApplyStatusRequest,
    {state: ApplyState; rejectValue: SubscriptionProcessExceptionCodeValues}
>('shoppingSubscription/fetchApplyStatus', async ({reservedId}, {getState, rejectWithValue, dispatch}) => {
    const appState = getState()
    const {seqID} = commonSelector(appState)

    if (!reservedId) {
        saveStateToStorage<FailSessionStorageInterface>({
            currentState: {failCode: FailCode.SUBSCRIPTION_FETCH_FAIL},
            secureStorageKey: 'orders.paymentmethod.shopping.fail',
            saverKey: seqID,
        })
        dispatch(navigateToShoppingSubscriptionFail({historyType: 'REPLACE'}))
        return rejectWithValue(SubscriptionProcessExceptionCode.SUBSCRIPTION_PROCESS_NOT_FOUND)
    }

    try {
        const {state} = await createWrapper<SchemasOrderSubscription['/apply/status']['response']>(
            api.get(`${API_BASE_URL}/status`, {
                params: {subscriptionProcessId: reservedId},
            }),
        )

        return state
    } catch (e) {
        Sentry.sendGeneralError(new Error('신청 상태 조회 실패'), e)

        if (isApiError(e)) {
            const subscriptionStatus =
                e.response?.data.result?.subscriptionStatus ??
                SubscriptionProcessExceptionCode.SUBSCRIPTION_PROCESS_FAILED_DEFAULT

            return rejectWithValue(subscriptionStatus)
        }

        return rejectWithValue(SubscriptionProcessExceptionCode.SUBSCRIPTION_PROCESS_FAILED_DEFAULT)
    }
})

export const apply = createAsyncThunk<
    ApplyPayload,
    ApplyRequest,
    {state: ApplyState; rejectValue: SubscriptionProcessExceptionCodeValues}
>('shoppingSubscription/apply', async ({reservedId}, {rejectWithValue}) => {
    try {
        const {state} = await createWrapper<SchemasOrderSubscription['/apply/apply']['response']>(
            api.post(`${API_BASE_URL}/apply`, {
                applyRequestBody: {sessionKey: null},
                deviceType,
                isMobileDisplay,
                osType,
                subscriptionProcessId: reservedId,
            }),
        )

        return state
    } catch (e) {
        Sentry.sendGeneralError(new Error('신청 실패'), e)

        if (isApiError(e)) {
            const subscriptionStatus =
                e.response?.data.result?.subscriptionStatus ??
                SubscriptionProcessExceptionCode.SUBSCRIPTION_PROCESS_FAILED_DEFAULT

            return rejectWithValue(subscriptionStatus)
        }

        return rejectWithValue(SubscriptionProcessExceptionCode.SUBSCRIPTION_PROCESS_FAILED_DEFAULT)
    }
})

export const requestImmediatePayment = createAsyncThunk<void, {reservedId: string}, {state: ApplyState}>(
    'shoppingSubscription/requestImmediatePayment',
    async ({reservedId}, {dispatch, getState}) => {
        const {seqID} = commonSelector(getState())

        try {
            const {
                data: {
                    result: {operationResult, contents},
                },
            } = await api.post<ApigwResponseBase<SchemasOrderSubscription['/apply/immediatePayment']['response']>>(
                `${API_BASE_URL}/immediatePayment`,
                {
                    paySubscriptionId: reservedId,
                },
            )
            if (operationResult !== 'SUCCESS') {
                saveStateToStorage<FailSessionStorageInterface>({
                    currentState: {
                        failCode: FailCode.IMMEDIATE_PAYMENT_FAIL,
                        immediatePaymentFailReason: contents.reasonForCustomer,
                        deliveryId: contents.deliveryId,
                    },
                    secureStorageKey: 'orders.paymentmethod.shopping.fail',
                    saverKey: seqID,
                })
                dispatch(navigateToShoppingSubscriptionFail({historyType: 'REPLACE'}))
                return
            }

            dispatch(
                changeLocation({
                    type: 'REPLACE',
                    url: `${SUBSCRIPTION_BASE_PATH}/${reservedId}/result`,
                }),
            )
        } catch (e) {
            Sentry.sendGeneralError(new Error('즉시결제 요청 실패'), e)

            // 요청 자체가 실패한 경우 상태를 모르므로 결과조회 실패로 처리
            saveStateToStorage<FailSessionStorageInterface>({
                currentState: {failCode: FailCode.SUBSCRIPTION_RESULT_FETCH_FAIL},
                secureStorageKey: 'orders.paymentmethod.shopping.fail',
                saverKey: seqID,
            })
            dispatch(navigateToShoppingSubscriptionFail({historyType: 'REPLACE'}))
        }
    },
)
